import { music, type MusicTrack } from "@bd/content";

/**
 * All sound is made in code, like the art: little WebAudio synths for effects and a chiptune band per era.
 * Nothing plays until the first click or key press (browsers require it).
 */

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let musicBus: GainNode | null = null;
let sfxBus: GainNode | null = null;

function readVol(key: string, d: number): number {
  try {
    const v = localStorage.getItem(key);
    return v === null ? d : Math.max(0, Math.min(1, Number(v)));
  } catch {
    return d;
  }
}

export const volume = { music: readVol("bd:vol:music", 0.35), sfx: readVol("bd:vol:sfx", 0.6) };

export function setVolume(kind: "music" | "sfx", v: number) {
  volume[kind] = v;
  try {
    localStorage.setItem(`bd:vol:${kind}`, String(v));
  } catch {
    // storage blocked: the setting lasts until the page closes
  }
  if (kind === "music" && musicBus) musicBus.gain.value = v * 0.5;
  if (kind === "sfx" && sfxBus) sfxBus.gain.value = v;
}

function audio(): AudioContext | null {
  if (ctx) return ctx;
  const AC = (window as unknown as { AudioContext?: typeof AudioContext }).AudioContext;
  if (!AC) return null;
  ctx = new AC();
  master = ctx.createGain();
  master.gain.value = 0.8;
  master.connect(ctx.destination);
  musicBus = ctx.createGain();
  musicBus.gain.value = volume.music * 0.5;
  musicBus.connect(master);
  sfxBus = ctx.createGain();
  sfxBus.gain.value = volume.sfx;
  sfxBus.connect(master);
  return ctx;
}

/** Start sound on the first gesture, then keep the music going. */
export function unlockAudio() {
  const a = audio();
  if (a && a.state === "suspended") void a.resume();
  startMusic();
}

function tone(freq: number, start: number, dur: number, type: OscillatorType, gain: number, bus: GainNode, slide = 0) {
  const a = ctx!;
  const o = a.createOscillator();
  const g = a.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, start);
  if (slide) o.frequency.exponentialRampToValueAtTime(Math.max(30, freq * slide), start + dur);
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(gain, start + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  o.connect(g).connect(bus);
  o.start(start);
  o.stop(start + dur + 0.02);
}

function noise(start: number, dur: number, gain: number, bus: GainNode, hp = 800) {
  const a = ctx!;
  const len = Math.max(1, Math.floor(a.sampleRate * dur));
  const buf = a.createBuffer(1, len, a.sampleRate);
  const d = buf.getChannelData(0);
  let s = 12345;
  for (let i = 0; i < len; i++) {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    d[i] = (s / 0x7fffffff) * 2 - 1;
  }
  const src = a.createBufferSource();
  src.buffer = buf;
  const f = a.createBiquadFilter();
  f.type = "highpass";
  f.frequency.value = hp;
  const g = a.createGain();
  g.gain.setValueAtTime(gain, start);
  g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  src.connect(f).connect(g).connect(bus);
  src.start(start);
}

export type Sfx = "stamp" | "fanfare" | "chime" | "thud" | "dice" | "card" | "cash" | "toast" | "levelup" | "click" | "build" | "blip";

/** Big moments push the music down for a second so they land. */
const DUCK: Partial<Record<Sfx, number>> = { fanfare: 1.4, levelup: 1, stamp: 0.6 };

/** Play a little sound effect. Quiet no-op before the first click, or if sound is off. */
export function sfx(name: Sfx, pitch = 1) {
  if (!ctx || !sfxBus || volume.sfx <= 0) return;
  const t = ctx.currentTime + 0.01;
  const b = sfxBus;
  const d = DUCK[name];
  if (d && musicBus) {
    const g = musicBus.gain;
    g.cancelScheduledValues(t);
    g.setValueAtTime(volume.music * 0.5, t);
    g.linearRampToValueAtTime(volume.music * 0.15, t + 0.05);
    g.linearRampToValueAtTime(volume.music * 0.5, t + d);
  }
  switch (name) {
    // a speech blip: a short chirp, pitched per speaker
    case "blip": tone(420 * pitch, t, 0.045, "square", 0.05, b, 1.15); break;
    case "stamp": noise(t, 0.08, 0.5, b, 300); tone(110, t, 0.14, "square", 0.25, b, 0.6); break;
    case "fanfare": [523, 659, 784, 1047].forEach((f, i) => tone(f, t + i * 0.09, 0.3, "square", 0.18, b)); break;
    case "chime": tone(880, t, 0.4, "triangle", 0.25, b); tone(1320, t + 0.08, 0.35, "triangle", 0.15, b); break;
    case "thud": tone(90, t, 0.3, "sine", 0.4, b, 0.5); break;
    case "dice": for (let i = 0; i < 5; i++) noise(t + i * 0.045, 0.03, 0.35, b, 2500); break;
    case "card": noise(t, 0.06, 0.25, b, 4000); break;
    case "cash": tone(1568, t, 0.12, "square", 0.15, b); tone(2093, t + 0.07, 0.35, "triangle", 0.2, b); noise(t, 0.05, 0.2, b, 3000); break;
    case "toast": tone(660, t, 0.1, "triangle", 0.12, b); tone(990, t + 0.06, 0.12, "triangle", 0.1, b); break;
    case "levelup": [392, 523, 659, 784, 1047].forEach((f, i) => tone(f, t + i * 0.06, 0.18, "square", 0.12, b)); break;
    case "click": tone(1200, t, 0.03, "square", 0.06, b); break;
    case "build": noise(t, 0.12, 0.3, b, 200); tone(160, t, 0.2, "square", 0.15, b, 0.7); break;
  }
}

// ------------------------------------------------------------------ music: a little band for each era

const KEYS: Record<string, number> = { C: 0, "C#": 1, Db: 1, D: 2, "D#": 3, Eb: 3, E: 4, F: 5, "F#": 6, Gb: 6, G: 7, "G#": 8, Ab: 8, A: 9, "A#": 10, Bb: 10, B: 11 };
const MAJOR = [0, 2, 4, 5, 7, 9, 11];
const MINOR = [0, 2, 3, 5, 7, 8, 10];
const DORIAN = [0, 2, 3, 5, 7, 9, 10];
const LYDIAN = [0, 2, 4, 6, 7, 9, 11];
const midi = (n: number) => 440 * Math.pow(2, (n - 69) / 12);

/** Melody shapes, as scale steps from the chord root (null = rest). Each section picks its own. */
const PATTERNS: (number | null)[][] = [
  [0, 2, 4, 2, 5, 4, 2, 1],
  [4, null, 4, 5, 4, 2, 0, null],
  [0, 1, 2, 4, null, 2, 1, 0],
  [7, 6, 4, null, 5, 4, 2, 4],
  [2, null, 2, 4, 2, null, 0, 1],
  [0, 4, 7, 4, 6, 4, 2, null],
];
/** How the bass moves each bar: root only, root-fifth, or walking. */
const BASS = [
  [0, null, 0, null, 0, null, 0, null],
  [0, null, 4, null, 0, null, 4, null],
  [0, null, 2, null, 4, null, 5, null],
];

function scaleFor(mood: string): number[] {
  if (mood === "minor") return MINOR;
  if (mood === "warm") return DORIAN;
  if (mood === "dreamy") return LYDIAN;
  return MAJOR;
}

function leadWave(lead: string): OscillatorType {
  if (lead === "saw") return "sawtooth";
  if (lead === "triangle") return "triangle";
  if (lead === "sine") return "sine";
  return "square";
}

let era = "parlour";
let track: MusicTrack | null = null;
let timer: number | null = null;
let nextBeat = 0;
let beat = 0;
/** each pass through a track picks new melody and bass shapes; after a few passes the next track plays */
let sections = { melody: 0, bass: 0 };
const PASSES_PER_TRACK = 6;

function pickTrack(exclude?: string) {
  const options = music.filter((m) => m.era === era);
  const pool = options.length > 1 ? options.filter((m) => m.id !== exclude) : options;
  track = pool[Math.floor(Math.random() * pool.length)] ?? music[0] ?? null;
  beat = 0;
  newSection();
}

function newSection() {
  sections = { melody: Math.floor(Math.random() * PATTERNS.length), bass: Math.floor(Math.random() * BASS.length) };
}

/** Follow the game's era: a new era brings its own tracks. */
export function setMusicEra(id: string) {
  if (id === era && track) return;
  era = id;
  pickTrack();
}

/** The track playing now, for the settings panel. */
export function nowPlaying(): string | null {
  return track?.name ?? null;
}

function startMusic() {
  if (timer !== null || !ctx) return;
  if (!track) pickTrack();
  nextBeat = ctx.currentTime + 0.1;
  // schedule a little ahead, a few times a second: light on the CPU, steady in time
  timer = window.setInterval(schedule, 200);
  // a hidden tab goes quiet and costs nothing
  document.addEventListener("visibilitychange", () => {
    if (!ctx) return;
    if (document.hidden) void ctx.suspend();
    else {
      void ctx.resume();
      nextBeat = ctx.currentTime + 0.1;
    }
  });
}

function schedule() {
  if (!ctx || !musicBus || !track || volume.music <= 0 || ctx.state !== "running") return;
  const spb = 60 / track.bpm / 2; // eighth notes
  const scale = scaleFor(track.mood);
  const root = 48 + (KEYS[track.key] ?? 0);
  const lead = leadWave(track.lead);
  const bars = track.progression.length;
  const deg7 = (d: number) => scale[((d % 7) + 7) % 7] + 12 * Math.floor(d / 7);
  while (nextBeat < ctx.currentTime + 0.6) {
    const barN = Math.floor(beat / 8);
    const bar = barN % bars;
    const pass = Math.floor(barN / bars);
    const step = beat % 8;
    if (step === 0 && bar === 0 && barN > 0) {
      if (pass % PASSES_PER_TRACK === 0) {
        pickTrack(track.id);
        return; // the next tick plays the new track in its own tempo and key
      }
      if (pass % 2 === 0) newSection();
    }
    const deg = track.progression[bar];
    // bass: the section's shape, on scale steps from the chord
    const b = BASS[sections.bass][step];
    if (b !== null) tone(midi(root - 12 + deg7(deg + b)), nextBeat, spb * 1.7, "triangle", 0.2, musicBus);
    // chord: a triad built from the scale (so minor keys get minor chords where they should)
    if (step % 4 === 2) [0, 2, 4].forEach((iv) => tone(midi(root + 12 + deg7(deg + iv)), nextBeat, spb * 0.9, "square", 0.022, musicBus!));
    // melody: the section's pattern; the last bar of each pass resolves home
    const pat = PATTERNS[(sections.melody + (pass % 2)) % PATTERNS.length];
    const m = bar === bars - 1 && step >= 6 ? (step === 6 ? 0 : null) : pat[step];
    if (m !== null) tone(midi(root + 24 + deg7(deg + m)), nextBeat, spb * (lead === "sine" ? 1.4 : 0.85), lead, lead === "sine" ? 0.07 : 0.045, musicBus);
    // soft percussion: a kick on the beat, a tick on the off-beats (not in the first pass, so tracks ease in)
    if (pass > 0 && step % 4 === 0) tone(70, nextBeat, 0.12, "sine", 0.18, musicBus, 0.5);
    if (pass > 0 && step % 2 === 1) noise(nextBeat, 0.025, 0.04, musicBus, 6000);
    nextBeat += spb;
    beat++;
  }
}
